import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, XCircle, Trash, Users as UsersIcon, ShieldCheck, ClockCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const statusPill = {
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function Users() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
      if (e.response?.status === 403) navigate("/");
    } finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const update = async (id, patch, label) => {
    try {
      await api.put(`/users/${id}`, patch);
      toast.success(label || "Updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/users/${toDelete.id}`);
      toast.success("User removed");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const pending = items.filter((u) => u.status === "pending");
  const active = items.filter((u) => u.status !== "pending");

  if (user?.role !== "admin") {
    return <div className="text-slate-500 text-sm">Admin access required.</div>;
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Access control</div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">Team</h1>
          <p className="text-sm text-slate-500 mt-1">Approve new sign-ups and manage roles.</p>
        </div>
        <Link
          to="/audit-log"
          className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-blue-700 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-md px-3 py-2 transition-colors shrink-0"
          data-testid="audit-log-shortcut"
        >
          <ClockCounterClockwise size={14} weight="duotone" /> Payment audit log
        </Link>
      </div>

      {pending.length > 0 && (
        <Card className="border border-amber-200 bg-amber-50/40 shadow-none" data-testid="pending-approvals-card">
          <div className="px-5 py-4 border-b border-amber-200 flex items-center gap-2">
            <ShieldCheck size={18} className="text-amber-600" weight="duotone" />
            <div>
              <div className="font-heading font-semibold text-slate-900">Awaiting approval</div>
              <div className="text-xs text-slate-600">{pending.length} sign-up{pending.length > 1 ? "s" : ""} pending review</div>
            </div>
          </div>
          <div className="divide-y divide-amber-100">
            {pending.map((u) => (
              <div key={u.id} className="px-4 sm:px-5 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-testid={`pending-user-${u.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{u.name}</div>
                  <div className="text-xs text-slate-500 truncate">{u.email}</div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    onClick={() => update(u.id, { status: "approved" }, "User approved")}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 flex-1 sm:flex-none"
                    data-testid={`approve-${u.id}`}
                  >
                    <CheckCircle size={14} className="mr-1" /> Approve
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => update(u.id, { status: "rejected" }, "User rejected")}
                    className="h-8 border-slate-300 flex-1 sm:flex-none"
                    data-testid={`reject-${u.id}`}
                  >
                    <XCircle size={14} className="mr-1" /> Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="border border-slate-200 shadow-none overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <UsersIcon size={18} className="text-slate-500" weight="duotone" />
          <div className="font-heading font-semibold text-slate-900">Team members</div>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-500">Loading…</TableCell></TableRow>
              ) : active.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-500">No team members yet</TableCell></TableRow>
              ) : active.map((u) => {
                const isSelf = u.id === user.id;
                return (
                  <TableRow key={u.id} className="hover:bg-slate-50/60" data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium text-slate-900">{u.name}{isSelf && <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-400">You</span>}</TableCell>
                    <TableCell className="text-sm text-slate-700">{u.email}</TableCell>
                    <TableCell>
                      <Select
                        value={u.role}
                        onValueChange={(v) => update(u.id, { role: v }, "Role updated")}
                        disabled={isSelf}
                      >
                        <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`role-select-${u.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="executive">Executive</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`${statusPill[u.status] || statusPill.approved} rounded-full text-[11px]`}>
                        {u.status || "approved"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {u.status === "rejected" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => update(u.id, { status: "approved" }, "Restored")}
                          className="h-8 mr-2"
                          data-testid={`restore-${u.id}`}
                        >
                          Restore
                        </Button>
                      ) : u.status === "approved" && !isSelf ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => update(u.id, { status: "rejected" }, "Access revoked")}
                          className="h-8 mr-2 text-rose-600 border-rose-200 hover:bg-rose-50"
                          data-testid={`revoke-${u.id}`}
                        >
                          Revoke
                        </Button>
                      ) : null}
                      {!isSelf && (
                        <button
                          onClick={() => setToDelete(u)}
                          className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash size={14} />
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {toDelete?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes the user account. Campaigns and payments they created stay intact.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-delete-user">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
